import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, apiService } from '../../services/apiService';

interface FactEntry {
  fact_id: string;
  fact: string;
  type: string;
  value: string;
  unit: string;
  source_text: string;
  source_paragraph: number;
  importance: string;
  attribution: string;
  confidence: number;
  status: string;
}

interface ArticleResult {
  id: string;
  headline: string;
  subheading: string;
  content: string;
  html_content: string;
  category: string;
  sub_vertical: string;
  tags: string[];
  style_match_score: number;
  confidence_score: number;
  rule_violations: string[];
  fidelity_result: any;
  news_source: string;
  news_url: string;
}

interface FactBankResult {
  frozen: boolean;
  total_facts: number;
  facts: FactEntry[];
  core_story: Record<string, any>;
  metadata: Record<string, any>;
}

interface PipelineResponse {
  status: string;
  message?: string;
  access_status?: string;
  recovered_from?: { source: string; url: string } | null;
  url: string;
  title: string;
  source: string;
  steps_executed: string[];
  total_steps: number;
  article: ArticleResult | null;
  fact_bank: FactBankResult | null;
  error: string;
}

type TabId = 'article' | 'factbank' | 'fidelity' | 'pipeline';

const IMPORTANCE_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  important: 'bg-amber-100 text-amber-700 border-amber-200',
  optional: 'bg-slate-100 text-slate-600 border-slate-200',
};

const TYPE_COLORS: Record<string, string> = {
  numeric: 'text-blue-600',
  financial: 'text-emerald-600',
  quote: 'text-purple-600',
  entity: 'text-cyan-600',
  date: 'text-orange-600',
  claim: 'text-amber-600',
  operational: 'text-teal-600',
  contract: 'text-indigo-600',
  ownership: 'text-pink-600',
  relationship: 'text-slate-600',
};

const UrlToArticle: React.FC = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<PipelineResponse | null>(null);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState<{ message: string; detail: string; accessStatus: string } | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('article');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  const handleSaveToLibrary = async () => {
    if (!result?.article) return;
    setSaving(true);
    try {
      // The URL-to-article pipeline may already have persisted the article on the backend.
      // Fetch the full article list and look for a match by headline.
      const data = await apiService.getArticles();
      const match = data?.articles?.find(
        (a: any) => a.headline === result.article!.headline || a.id === result.article!.id
      );
      if (match) {
        setSavedId(match.id);
      } else {
        // Article not yet in library — navigate to articles list so user can see it
        navigate('/content');
      }
    } catch {
      navigate('/content');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim() || loading) return;

    setLoading(true);
    setError('');
    setUnavailable(null);
    setResult(null);
    setProgress('Fetching and scraping URL...');
    setElapsedTime(0);

    const timer = setInterval(() => setElapsedTime(t => t + 1), 1000);

    try {
      const response = await fetch(`${API_URL}/api/url-to-article`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        // ITEM 5: a paywalled / sign-in / cookie-walled / unextractable source
        // returns status 'source_unavailable' — surface a clear, dedicated
        // "Source content unavailable" message rather than a generic failure.
        if (data.status === 'source_unavailable') {
          setUnavailable({
            message: data.message || 'Source content unavailable',
            detail: data.error || '',
            accessStatus: data.access_status || '',
          });
        } else {
          setError(data.error || `HTTP ${response.status}`);
        }
      } else if (data.status === 'source_unavailable') {
        setUnavailable({
          message: data.message || 'Source content unavailable',
          detail: data.error || '',
          accessStatus: data.access_status || '',
        });
      } else {
        setResult(data);
        if (data.recovered_from) {
          // Non-fatal notice: original source was blocked, accessible coverage used.
          setResult({ ...data });
        }
        if (data.article) {
          setActiveTab('article');
        } else if (data.fact_bank) {
          setActiveTab('factbank');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      clearInterval(timer);
      setLoading(false);
      setProgress('');
    }
  };

  const tabs: { id: TabId; label: string; icon: string; show: boolean }[] = [
    { id: 'article', label: 'Article', icon: 'article', show: !!result?.article },
    { id: 'factbank', label: 'Fact Bank', icon: 'database', show: !!result?.fact_bank },
    { id: 'fidelity', label: 'Fidelity', icon: 'verified', show: !!result?.article?.fidelity_result },
    { id: 'pipeline', label: 'Pipeline', icon: 'account_tree', show: !!result },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="font-display text-headline-lg text-on-surface mb-1">URL to Article</h1>
        <p className="font-body text-body-md text-on-surface-variant">
          Paste a news article URL. The pipeline will scrape it, extract facts, build a Fact Bank, and generate an AlCircle-style article.
        </p>
      </div>

      {/* URL Input */}
      <form onSubmit={handleSubmit} className="mb-8">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">link</span>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://www.reuters.com/business/aluminium-..."
              required
              disabled={loading}
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl font-body text-body-md text-on-surface placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all disabled:opacity-60"
              style={{ '--tw-ring-color': 'rgba(79,70,229,0.25)' } as React.CSSProperties}
              onFocus={e => { e.currentTarget.style.borderColor = '#4f46e5'; }}
              onBlur={e => { e.currentTarget.style.borderColor = ''; }}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="px-8 py-3.5 text-white font-display text-label-md rounded-xl transition-all flex items-center gap-2 shrink-0 disabled:opacity-50"
            style={{ background: loading || !url.trim() ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', boxShadow: loading || !url.trim() ? 'none' : '0 4px 16px rgba(79,70,229,0.3)' }}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[20px]">bolt</span>
                Generate Article
              </>
            )}
          </button>
        </div>
      </form>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(79,70,229,0.1)' }}>
              <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'rgba(79,70,229,0.3)', borderTopColor: '#4f46e5' }} />
            </div>
            <div>
              <p className="font-display text-headline-sm text-on-surface">Pipeline Running</p>
              <p className="font-body text-body-sm text-on-surface-variant">{progress} ({elapsedTime}s elapsed)</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {['Source Extraction', 'Fact Verification', 'Fact Bank', 'Article Generation', 'Fidelity Check', 'Style Check', 'Final QA'].map((step, i) => (
              <span key={step} className="px-3 py-1 bg-slate-100 text-on-surface-variant rounded-full font-mono text-label-xs">
                {step}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Source content unavailable (paywall / sign-in / cookie wall / unextractable) */}
      {unavailable && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-amber-500 text-[24px] mt-0.5">block</span>
            <div>
              <p className="font-display text-headline-sm text-amber-900 mb-1">{unavailable.message}</p>
              {unavailable.detail && (
                <p className="font-body text-body-md text-amber-700">{unavailable.detail}</p>
              )}
              {unavailable.accessStatus && (
                <span className="inline-block mt-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-mono text-label-xs">
                  {unavailable.accessStatus}
                </span>
              )}
              <p className="font-body text-body-sm text-amber-600 mt-3">
                No article was generated. The pipeline does not fabricate content from a headline or
                metadata — try a different, accessible source URL for this story.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-red-500 text-[24px] mt-0.5">error</span>
            <div>
              <p className="font-display text-headline-sm text-red-900 mb-1">Pipeline Failed</p>
              <p className="font-body text-body-md text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {result.recovered_from && (
            <div className="rounded-2xl p-4 mb-4"
              style={{ background: 'rgba(79,70,229,0.07)', border: '1px solid rgba(79,70,229,0.25)' }}>
              <p className="font-body text-body-sm flex items-center gap-2" style={{ color: '#92400e' }}>
                <span className="material-symbols-outlined text-[18px]">swap_horiz</span>
                Original source was unavailable/blocked — generated from accessible coverage:{' '}
                <span className="font-medium">{result.recovered_from.source}</span>
              </p>
            </div>
          )}
          {/* Summary Bar */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className={`material-symbols-outlined text-[28px] ${result.article ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {result.article ? 'check_circle' : 'warning'}
                </span>
                <div>
                  <p className="font-display text-headline-sm text-on-surface">{result.title}</p>
                  <p className="font-body text-body-sm text-on-surface-variant">
                    Source: {result.source} | {result.total_steps} pipeline steps | {elapsedTime}s
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {result.fact_bank && (
                  <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg font-mono text-label-xs flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">lock</span>
                    {result.fact_bank.total_facts} facts frozen
                  </span>
                )}
                {result.article && (
                  <span className="px-3 py-1.5 rounded-lg font-mono text-label-xs"
                    style={{ background: 'rgba(79,70,229,0.1)', color: '#3730a3' }}>
                    {result.article.category}
                  </span>
                )}
                {result.article && (
                  savedId ? (
                    <button
                      onClick={() => navigate(`/article/${savedId}`)}
                      className="px-3 py-1.5 rounded-lg font-mono text-label-xs flex items-center gap-1.5 transition-all"
                      style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.35)', color: '#065f46' }}
                    >
                      <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                      Open in editor
                    </button>
                  ) : (
                    <button
                      onClick={handleSaveToLibrary}
                      disabled={saving}
                      className="px-3 py-1.5 rounded-lg font-mono text-label-xs flex items-center gap-1.5 transition-all disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', color: '#ffffff', boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }}
                    >
                      <span className="material-symbols-outlined text-[14px]">{saving ? 'progress_activity' : 'library_add'}</span>
                      {saving ? 'Checking…' : 'Save to library'}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 p-1 rounded-xl w-fit" style={{ background: '#f1f5f9' }}>
            {tabs.filter(t => t.show).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-4 py-2 rounded-lg font-display text-label-md flex items-center gap-1.5 transition-all"
                style={{
                  background: activeTab === tab.id ? '#ffffff' : 'transparent',
                  color: activeTab === tab.id ? '#4f46e5' : '#64748b',
                  boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Article Tab */}
          {activeTab === 'article' && result.article && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-8">
                <h2 className="font-display text-headline-md text-on-surface mb-2">{result.article.headline}</h2>
                <p className="font-body text-body-md text-on-surface-variant mb-6">{result.article.subheading}</p>

                <div className="flex gap-2 mb-6 flex-wrap">
                  {result.article.tags?.map(tag => (
                    <span key={tag} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md font-mono text-label-xs">{tag}</span>
                  ))}
                </div>

                <div className="prose prose-slate max-w-none font-body text-body-md leading-relaxed">
                  {result.article.content.split('\n\n').map((para, i) => (
                    <p key={i} className="mb-4">{para}</p>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100 flex gap-4 text-slate-400 font-mono text-label-xs">
                  <span>Source: {result.article.news_source}</span>
                  <span>Style: {result.article.style_match_score}/100</span>
                  <span>Confidence: {result.article.confidence_score}/100</span>
                </div>

                {result.article.rule_violations?.length > 0 && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="font-display text-label-md text-amber-800 mb-2">Rule Violations ({result.article.rule_violations.length})</p>
                    <ul className="space-y-1">
                      {result.article.rule_violations.map((v, i) => (
                        <li key={i} className="font-body text-body-sm text-amber-700 flex items-start gap-2">
                          <span className="material-symbols-outlined text-[14px] mt-0.5 shrink-0">warning</span>
                          {v}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fact Bank Tab */}
          {activeTab === 'factbank' && result.fact_bank && (
            <div className="space-y-4">
              {/* Core Story */}
              {result.fact_bank.core_story?.what_happened && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="font-display text-headline-sm text-on-surface mb-3 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[20px]" style={{ color: '#4f46e5' }}>auto_stories</span>
                    Core Story
                  </h3>
                  <p className="font-body text-body-md text-on-surface mb-3">{result.fact_bank.core_story.what_happened}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['where', 'when', 'significance'].map(field => {
                      const val = result.fact_bank!.core_story[field];
                      return val ? (
                        <div key={field} className="bg-slate-50 rounded-lg p-3">
                          <span className="font-mono text-label-xs text-slate-400 uppercase">{field}</span>
                          <p className="font-body text-body-sm text-on-surface mt-1">{val}</p>
                        </div>
                      ) : null;
                    })}
                    {result.fact_bank.core_story.who_involved?.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <span className="font-mono text-label-xs text-slate-400 uppercase">who</span>
                        <p className="font-body text-body-sm text-on-surface mt-1">{result.fact_bank.core_story.who_involved.join(', ')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Facts Table */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <h3 className="font-display text-headline-sm text-on-surface flex items-center gap-2">
                    <span className="material-symbols-outlined text-[20px] text-emerald-500">lock</span>
                    Approved Fact Bank ({result.fact_bank.total_facts} facts — frozen)
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-3 text-left font-mono text-label-xs text-on-surface-variant uppercase">ID</th>
                        <th className="px-4 py-3 text-left font-mono text-label-xs text-on-surface-variant uppercase">Fact</th>
                        <th className="px-4 py-3 text-left font-mono text-label-xs text-on-surface-variant uppercase">Type</th>
                        <th className="px-4 py-3 text-left font-mono text-label-xs text-on-surface-variant uppercase">Value</th>
                        <th className="px-4 py-3 text-left font-mono text-label-xs text-on-surface-variant uppercase">Importance</th>
                        <th className="px-4 py-3 text-left font-mono text-label-xs text-on-surface-variant uppercase">Attribution</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.fact_bank.facts.map(fact => (
                        <tr key={fact.fact_id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-label-sm whitespace-nowrap" style={{ color: '#4f46e5' }}>{fact.fact_id}</td>
                          <td className="px-4 py-3 font-body text-body-sm text-on-surface max-w-xs">
                            {fact.fact}
                            {fact.source_text && (
                              <p className="text-slate-400 text-body-sm mt-1 italic truncate max-w-sm" title={fact.source_text}>
                                &ldquo;{fact.source_text.slice(0, 80)}{fact.source_text.length > 80 ? '...' : ''}&rdquo;
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-mono text-label-xs ${TYPE_COLORS[fact.type] || 'text-on-surface-variant'}`}>
                              {fact.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-label-sm text-on-surface whitespace-nowrap">
                            {fact.value}{fact.unit ? ` ${fact.unit}` : ''}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded border font-mono text-label-xs ${IMPORTANCE_COLORS[fact.importance] || ''}`}>
                              {fact.importance}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-body text-body-sm text-on-surface-variant max-w-[200px] truncate">{fact.attribution || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Fidelity Tab */}
          {activeTab === 'fidelity' && result.article?.fidelity_result && (() => {
            const fr = result.article!.fidelity_result;
            return (
            <div className="space-y-6">
              {/* Overall Gate */}
              <div className={`rounded-2xl border p-6 ${fr.passed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-3">
                  <span className={`material-symbols-outlined text-[32px] ${fr.passed ? 'text-emerald-500' : 'text-red-500'}`}>
                    {fr.passed ? 'verified' : 'gpp_bad'}
                  </span>
                  <div>
                    <h3 className="font-display text-headline-md text-on-surface">
                      Fidelity Gate: {fr.passed ? 'ALL CHECKS PASSED' : 'FAILED'}
                    </h3>
                    <p className="font-body text-body-sm text-slate-600 mt-1">
                      All three checks must pass independently. A single failure blocks publication.
                    </p>
                  </div>
                </div>
              </div>

              {/* Three Checks Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Check 1: Accuracy */}
                <div className={`rounded-2xl border p-5 ${fr.accuracy_passed ? 'bg-white border-emerald-200' : 'bg-white border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`material-symbols-outlined text-[20px] ${fr.accuracy_passed ? 'text-emerald-500' : 'text-red-500'}`}>
                      {fr.accuracy_passed ? 'check_circle' : 'cancel'}
                    </span>
                    <h4 className="font-display text-headline-sm text-on-surface">Accuracy</h4>
                  </div>
                  <p className="font-body text-body-sm text-on-surface-variant mb-3">Did the article change any facts?</p>
                  <p className={`font-display text-headline-lg ${fr.accuracy_passed ? 'text-emerald-600' : 'text-red-600'}`}>
                    {fr.accuracy_errors?.length || 0} errors
                  </p>
                  <p className="font-mono text-label-xs text-slate-400 mt-1">Required: 0</p>
                  {fr.accuracy_errors?.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {fr.accuracy_errors.slice(0, 5).map((e: any, i: number) => (
                        <li key={i} className="text-body-sm text-red-700 bg-red-50 rounded-lg p-2">
                          <span className="font-mono text-label-xs text-red-500">[{e.fact_id}]</span>{' '}
                          {e.check}: expected <strong>{e.expected}</strong>, found <strong>{e.found}</strong>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Check 2: Coverage */}
                <div className={`rounded-2xl border p-5 ${fr.coverage_passed ? 'bg-white border-emerald-200' : 'bg-white border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`material-symbols-outlined text-[20px] ${fr.coverage_passed ? 'text-emerald-500' : 'text-red-500'}`}>
                      {fr.coverage_passed ? 'check_circle' : 'cancel'}
                    </span>
                    <h4 className="font-display text-headline-sm text-on-surface">Coverage</h4>
                  </div>
                  <p className="font-body text-body-sm text-on-surface-variant mb-3">Did the article omit important facts?</p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-label-xs text-on-surface-variant">Critical</span>
                      <span className={`font-mono text-label-sm ${fr.missing_critical_fact_ids?.length === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fr.critical_coverage} {fr.missing_critical_fact_ids?.length === 0 ? '' : '(FAIL)'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-label-xs text-on-surface-variant">Important</span>
                      <span className="font-mono text-label-sm text-on-surface">{fr.important_coverage}</span>
                    </div>
                  </div>
                  <p className="font-mono text-label-xs text-slate-400 mt-2">Required: Critical 100%, Important ≥90%</p>
                  {fr.missing_critical_fact_ids?.length > 0 && (
                    <div className="mt-3">
                      <p className="font-mono text-label-xs text-red-600 mb-1">Missing critical:</p>
                      {fr.missing_critical_fact_ids.map((fid: string) => (
                        <span key={fid} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-red-100 text-red-700 rounded font-mono text-label-xs">{fid}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Check 3: Unsupported */}
                <div className={`rounded-2xl border p-5 ${fr.unsupported_passed ? 'bg-white border-emerald-200' : 'bg-white border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`material-symbols-outlined text-[20px] ${fr.unsupported_passed ? 'text-emerald-500' : 'text-red-500'}`}>
                      {fr.unsupported_passed ? 'check_circle' : 'cancel'}
                    </span>
                    <h4 className="font-display text-headline-sm text-on-surface">Unsupported</h4>
                  </div>
                  <p className="font-body text-body-sm text-on-surface-variant mb-3">Did the article add facts not in the source?</p>
                  <p className={`font-display text-headline-lg ${fr.unsupported_passed ? 'text-emerald-600' : 'text-red-600'}`}>
                    {(fr.unsupported_facts?.length || 0) + (fr.unsupported_entities?.length || 0)} claims
                  </p>
                  <p className="font-mono text-label-xs text-slate-400 mt-1">Required: 0</p>
                  {(fr.unsupported_facts?.length > 0 || fr.unsupported_entities?.length > 0) && (
                    <ul className="mt-3 space-y-1">
                      {fr.unsupported_facts?.slice(0, 3).map((u: string, i: number) => (
                        <li key={i} className="text-body-sm text-orange-700 bg-orange-50 rounded-lg p-2">{u}</li>
                      ))}
                      {fr.unsupported_entities?.slice(0, 3).map((e: string, i: number) => (
                        <li key={`e-${i}`} className="text-body-sm text-purple-700 bg-purple-50 rounded-lg p-2">Person: {e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Fact Coverage Matrix */}
              {fr.coverage_matrix?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-100">
                    <h3 className="font-display text-headline-sm text-on-surface">Fact Coverage Matrix</h3>
                    <p className="font-body text-body-sm text-on-surface-variant mt-1">Every fact from the Fact Bank and whether it appears in the generated article</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-4 py-3 text-left font-mono text-label-xs text-on-surface-variant uppercase">Fact</th>
                          <th className="px-4 py-3 text-center font-mono text-label-xs text-on-surface-variant uppercase">Importance</th>
                          <th className="px-4 py-3 text-center font-mono text-label-xs text-on-surface-variant uppercase">Generated?</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {fr.coverage_matrix.map((row: any) => (
                          <tr key={row.fact_id} className={`${!row.generated && row.importance === 'critical' ? 'bg-red-50' : ''}`}>
                            <td className="px-4 py-3">
                              <span className="font-mono text-label-xs mr-2" style={{ color: '#4f46e5' }}>{row.fact_id}</span>
                              <span className="font-body text-body-sm text-on-surface">{row.fact}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 rounded border font-mono text-label-xs ${IMPORTANCE_COLORS[row.importance] || ''}`}>
                                {row.importance}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {row.generated ? (
                                <span className="material-symbols-outlined text-emerald-500 text-[20px]">check_circle</span>
                              ) : (
                                <span className="material-symbols-outlined text-red-400 text-[20px]">cancel</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Pipeline Tab */}
          {activeTab === 'pipeline' && result && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-display text-headline-sm text-on-surface mb-4">Pipeline Execution</h3>
              <div className="flex flex-wrap gap-2">
                {result.steps_executed.map((step, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="px-3 py-1.5 bg-slate-100 rounded-lg font-mono text-label-xs text-on-surface">
                      {step.replace(/_/g, ' ')}
                    </span>
                    {i < result.steps_executed.length - 1 && (
                      <span className="material-symbols-outlined text-slate-300 text-[16px]">arrow_forward</span>
                    )}
                  </div>
                ))}
              </div>
              {result.error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="font-body text-body-sm text-red-700">{result.error}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UrlToArticle;
